import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { NgbCustomParserFormatter } from "components/common/date-picker/ngb-custom-parser-formatter";
import { AlertController, NavParams } from "ionic-angular";
import { commonVar } from "providers/common-var/common";
import { FacilitiesService } from "providers/facilities-service/facilities.service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Subscription } from "rxjs";
import {
  FacilityContact,
  FacilityDetail,
  Phone,
  StreetAddress,
} from "../../../models/facilities/facility-details";
import { isValidNpi } from "../../../utils/npi";

@Component({
  selector: "general-facility",
  templateUrl: "general-facility.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GeneralFacility),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => GeneralFacility),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class GeneralFacility implements OnInit, OnDestroy {
  // CO-3229 / REQ-009: the three facility types that do not require an NPI on
  // their own — Q5001 Home/Residence, Q5002 Assisted Living, Q5009 Place not
  // otherwise specified. Q5001 joins the set in CO-2419; the other two were
  // already exempt. Every other type requires one because a hospice bill for it
  // is invalid without it, and that requirement predates CO-2419.
  //
  // Must stay identical to npiExemptFacilityTypes in
  // facility-functions/src/lib/validator/npi.go. A relaxation in only one place
  // either blocks a legitimate save or admits a record that violates the
  // R&B-implies-NPI invariant (REQ-021).
  static readonly NPI_EXEMPT_FACILITY_TYPES = [
    "HOME",
    "ASSISTED_LIVING_FACILITY",
    "UNSPECIFIED",
  ];

  // CO-3228 / REQ-007, revised 2026-08-03 (Product via Maggie Rainwater): the
  // facility types that are ALWAYS Room & Board billable. Their checkbox is
  // checked and GRAYED OUT — not a default the operator can override, because
  // these types are R&B-billable by their nature.
  //
  // This set is deliberately the exact complement of NPI_EXEMPT_FACILITY_TYPES:
  // "always R&B billable" and "always requires an NPI" are the same statement
  // about the same seven types. Derived from that list rather than written out
  // twice, so the two cannot drift — the drift between two hand-maintained
  // copies of one rule is what caused CO-3517 (Java and Go disagreeing about
  // Q5009) and the nine repeats of the HOME-means-no-facility assumption.
  //
  // Q5001, Q5002 and Q5009 are the editable three: new records default
  // UNCHECKED, the operator may check or uncheck freely, and the box is never
  // grayed. Checking it makes the NPI required and unchecking it makes the NPI
  // optional again — which is already exactly what CO-3229's union rule does,
  // so no separate wiring is needed for that half.
  //
  // Previously the default-checked set was the four REQ-007 types and Q5009's
  // default was derived from NPI presence (AC-007-4). Product replaced both:
  // Q5005–Q5008 join the always-billable set, and Q5009 no longer derives its
  // default from the NPI.
  //
  // Expressed as a PREDICATE rather than a list on purpose. This epic has already
  // been bitten nine times by one rule written out in more than one place, so
  // adding an eleventh enumeration of facility types — to assert a fact
  // NPI_EXEMPT_FACILITY_TYPES already encodes — would repeat the mistake. A future
  // facility type is therefore always-billable unless explicitly made NPI-exempt,
  // which is the safe default: a type that requires an NPI is a type whose R&B
  // claim can name an operating provider.
  static isAlwaysRnbBillable(facilityType: string): boolean {
    return (
      !!facilityType &&
      GeneralFacility.NPI_EXEMPT_FACILITY_TYPES.indexOf(facilityType) === -1
    );
  }

  // CO-3238 / REQ-029 (BDD 1.3): the enum value behind "Q5001 - Home/Residence"
  // (commonVar.FACILITY_TYPES). Named rather than inlined because "HOME" reads as
  // a care-location in this codebase — the HOME-means-no-facility assumption this
  // epic has now corrected in ten places — and here it means the opposite: a real
  // facility whose site of service is a private residence.
  static readonly Q5001_FACILITY_TYPE = "HOME";

  // Copy is REQ-029's suggested wording, confirmed for this release. Informational
  // on purpose, not a warning: a customer may legitimately bill Q5001, so this
  // states what Q5001 means and what the usual alternative is, and stops there.
  static readonly Q5001_HINT =
    "Q5001 is the CMS site-of-service code for private residence " +
    "(house, apartment, townhouse). For congregate settings — group homes, " +
    "board-and-care, ICF/DD — the typical selection is Q5009 (Place not " +
    "otherwise specified).";

  // CO-3240 / REQ-026 (BDD 9.2): shown when the R&B flag has been moved off its
  // type default. States the consequence rather than scolding — REQ-026 exists to
  // "reduce accidental misconfiguration", and an operator who meant it needs to be
  // able to proceed without argument.
  static readonly RNB_OVERRIDE_WARNING =
    "Room and Board is not billable by default for this facility type. Leaving " +
    '"Billable Room and Board" checked will generate Room and Board claims for ' +
    "this facility and requires a valid NPI.";

  // CO-3240 / REQ-027 (BDD 9.3): matches the max=500 on Facility.Q5001RationaleNote
  // in facility-functions. Enforced here too so the operator is stopped at the
  // field rather than by a save that fails with no on-screen explanation.
  static readonly Q5001_NOTE_MAX_LENGTH = 500;

  private static readonly NPI_MESSAGES = {
    npiRequiredRnb: "NPI is required when Billable Room and Board is checked.",
    npiRequiredType: "NPI is required for this facility type.",
    npiFormat: "NPI must be a valid 10-digit National Provider Identifier.",
  };

  innerFacilityForm: FormGroup;
  _saveEnabled: boolean = false;
  subscriptions: Subscription[] = [];
  datePlacementPreference: PlacementArray = ["bottom-left"];
  effectiveDateMin: any = "";
  effectiveDateMax: any = "";
  isNPIRequired: boolean = true;
  // CO-3237 / REQ-018 (BDD 8.1): text for the polite live region next to the NPI
  // field. Empty until the requirement actually FLIPS, so opening a facility says
  // nothing — a live region that speaks on load is noise, and on load the label
  // itself already carries the state.
  npiRequirementAnnouncement: string = "";
  // CO-3407 (epic CO-2315, B.1.7): DHCS facility subclassification for
  // state-Medicaid R&B. Flag-gated; optional. RCFE is selectable (real
  // classification) but DHCS excludes it from R&B billing downstream.
  californiaMediCalSupportToggle: boolean = false;
  medicaidFacilitySubtype: string = "";
  medicaidSubtypeList = [
    { name: "NF — Nursing Facility", value: "NF" },
    { name: "SNF — Skilled Nursing Facility", value: "SNF" },
    { name: "ALF — Assisted Living Facility", value: "ALF" },
    { name: "B&C — Board and Care", value: "BC" },
    { name: "ICF — Intermediate Care Facility", value: "ICF" },
    { name: "ICF/DD — Intermediate Care (Devel. Disabled)", value: "ICF_DD" },
    { name: "RCFE — Residential Care Facility for the Elderly", value: "RCFE" },
  ];

  // CO-3238 / REQ-029 (BDD 1.3), CO-3240 / REQ-027 (BDD 9.3). "Q5001 is the
  // currently selected Facility Type" — derived from the selected type, not from
  // the change event, so it is also true when an operator OPENS a facility that
  // is already Q5001. REQ-029 words it as a state, not a transition.
  //
  // ONE flag drives BOTH the contextual hint and the rationale-note field,
  // because both requirements ask the same question and this epic has been bitten
  // ten times by one rule written out in more than one place. If they ever need to
  // diverge, that is a deliberate change to applyQ5001Selection(), not two fields
  // quietly drifting apart.
  q5001Selected: boolean = false;
  readonly q5001Hint: string = GeneralFacility.Q5001_HINT;
  // Carries the hint to a screen reader only when the selection actually CHANGES
  // to Q5001. Same split as npiRequirementAnnouncement above and for the same
  // reason: the visible hint is *ngIf'd, so making IT the live region would
  // announce a wall of text every time a Q5001 facility is opened.
  q5001HintAnnouncement: string = "";

  isBillableRNB: boolean = false;
  showIsBillable: boolean = false;
  // CO-3240 / REQ-026 (BDD 9.2): true when the operator has moved the R&B flag
  // away from its type default. Non-blocking; see applyRnbOverrideWarning().
  showRnbOverrideWarning: boolean = false;
  rnbOverrideAnnouncement: string = "";
  // Instance copies of the two statics the template needs; Angular templates
  // cannot reach a static class member.
  readonly rnbOverrideWarning: string = GeneralFacility.RNB_OVERRIDE_WARNING;
  readonly q5001NoteMaxLength: number = GeneralFacility.Q5001_NOTE_MAX_LENGTH;
  // CO-3228 (revised 2026-08-03): true when the R&B checkbox is grayed out
  // because the facility type is always R&B billable. Bound to [disabled] in the
  // template. See applyRnbLockState().
  isBillableRNBLocked: boolean = false;
  countyList: any[] = [];
  emailRegex = commonVar.EMAIL_REGEX;
  phoneMask = commonVar.PHONE_US_MASK;
  x = commonVar.ZIP_CODE_MASK;
  zipCodeExt = commonVar.ZIP_CODE_EXTENSION_MASK;
  stateList = commonVar.STATE_LIST;
  facilityTypes = commonVar.FACILITY_TYPES;
  // CO-2419: default-OFF. While false, Q5001 is filtered out of the Facility
  // Type selector so the type cannot be chosen at all (dark release).
  q5001Enabled: boolean = false;
  alphaRegex = commonVar.ALPHA_REGEX;
  alphaNumericRegx = commonVar.ALPHANUMERIC;
  numberRegex = commonVar.NUMBER_REGEX;

  @Input() isEditMode: boolean = false;
  @Input() isNew: boolean = false;
  @Output() deletePhoneNumber = new EventEmitter<any>();
  @Output() onTouched = new EventEmitter<boolean>();
  @Output() onContactRemoved = new EventEmitter<boolean>();
  @Input() currentFacility: any;
  @Output() onFacilitySaved = new EventEmitter<any>();
  @Output() onCanceled = new EventEmitter<any>();
  @Output() onBillableChange = new EventEmitter<any>();

  get phoneNumbers() {
    return this.innerFacilityForm.get("phoneNumbers") as FormArray;
  }

  set value(value: any) {
    if (value.phoneNumbers) {
      const phoneNumbersCount = value.phoneNumbers.length;
      let phoneNumbersControlsCount = this.phoneNumbers.controls.length;
      if (value.phoneNumbers[phoneNumbersControlsCount - 1].phoneNumber) {
        value.phoneNumbers[phoneNumbersControlsCount - 1].phoneNumber =
          value.phoneNumbers[phoneNumbersControlsCount - 1].phoneNumber.replace(
            /^(\d{3})(\d{3})(\d{4}).*/,
            "($1)$2-$3"
          );
      }
      while (phoneNumbersControlsCount !== phoneNumbersCount) {
        this.addPhoneNumber();
        phoneNumbersControlsCount = this.phoneNumbers.controls.length;
        if (value.phoneNumbers[phoneNumbersControlsCount - 1].phoneNumber) {
          value.phoneNumbers[phoneNumbersControlsCount - 1].phoneNumber =
            value.phoneNumbers[
              phoneNumbersControlsCount - 1
            ].phoneNumber.replace(/^(\d{3})(\d{3})(\d{4}).*/, "($1)$2-$3");
        }
      }
    }
  }

  set saveEnabled(value: boolean) {
    this._saveEnabled = value;
  }

  get saveEnabled() {
    return this._saveEnabled;
  }

  constructor(
    protected formBuilder: FormBuilder,
    protected alertController: AlertController,
    protected navParams: NavParams,
    private facilityService: FacilitiesService,
    private cd: ChangeDetectorRef,
    private stateManagementService: StateManagementService
  ) {}

  ngOnInit() {
    this.stateManagementService.q5001HomeResidenceToggle.subscribe((x) => {
      this.q5001Enabled = x != null ? x : this.q5001Enabled;
    });
    this.stateManagementService.californiaMediCalSupportToggle.subscribe(
      (enabled) => {
        this.californiaMediCalSupportToggle = !!enabled;
      }
    );
    this.getFacilityTypes();
    this.initInnerFacilityForm();
    this.innerFacilityForm.get("initialized").setValue(true);
    this.cd.detectChanges();
    // CO-3229: coerced, because the field is absent on legacy facility records and
    // isBillableRNB is declared boolean. mapFacilityToFormGroup() already set this
    // during initInnerFacilityForm() above; the assignment is kept so the
    // no-currentFacility path stays explicit.
    this.isBillableRNB = this.currentFacility
      ? !!this.currentFacility.isEligibleForRNB
      : false;
    this.applyRnbLockState(
      this.currentFacility ? this.currentFacility.facilityType : ""
    );
    // CO-3238: announce=false — opening a Q5001 facility shows the hint but says
    // nothing, because on load the operator has not made a choice to inform.
    this.applyQ5001Selection(
      this.currentFacility ? this.currentFacility.facilityType : "",
      false
    );
    // CO-3240 / REQ-026: also announce=false, and note this CAN be true on load —
    // a stored Q5001/Q5002/Q5009 facility with the flag checked already diverges
    // from its default. The warning is correct to show; narrating it on open is
    // not, because the operator has not just done anything.
    this.applyRnbOverrideWarning(
      this.currentFacility ? this.currentFacility.facilityType : "",
      false
    );
    this.medicaidFacilitySubtype = this.currentFacility
      ? (this.currentFacility as any).medicaidFacilitySubtype || ""
      : "";
  }

  getFacilityTypes() {
    // CO-2419 dark release: filter Q5001 out of BOTH the static fallback and
    // the service response while the flag is off, so the type is unavailable
    // whichever source is in play. Q-code rather than EnumValue because the
    // fallback list carries no enum and Q5001 is the stable identifier.
    this.facilityTypes = this.filterQ5001(commonVar.FACILITY_TYPES);
    this.facilityService.getFacilityTypes().subscribe(
      (response) => {
        if (response) {
          const sortArray = (sourceArray) => {
            const sortByLocation = (a, b) =>
              a.qCode.localeCompare(b.qCode, "en", { numeric: true });
            return sourceArray.sort(sortByLocation);
          };
          sortArray(response);
          this.facilityTypes = this.filterQ5001(response);
          this.facilityTypes.forEach((element) => {
            element.display =
              element["qCode"] + " - " + element["displayValue"];
          });
        }
      },
      (err) => {}
    );
  }

  private filterQ5001(types: any[]) {
    if (this.q5001Enabled || !types) {
      return types;
    }
    return types.filter(
      (t) =>
        String(t.qCode || t.display || t.value || "").indexOf("Q5001") === -1
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  valid(control: string) {
    return this.innerFacilityForm.get(control).valid;
  }

  touched(control: string) {
    return this.innerFacilityForm.get(control).touched;
  }

  validate(_: FormControl) {
    return this.innerFacilityForm.valid
      ? null
      : { generalDetails: { valid: false } };
  }

  // Phone Number
  addPhoneNumber() {
    const group = this.formBuilder.group({
      contactName: [null, Validators.pattern(this.alphaNumericRegx)],
      title: [null, Validators.pattern(this.alphaNumericRegx)],
      office: [null, [Validators.minLength(13)]],
      mobile: [null, [Validators.minLength(13)]],
      fax: [null, [Validators.minLength(13)]],
      email: [null, [Validators.pattern(this.emailRegex)]],
      id: [null],
      deleted: [false],
    });

    this.phoneNumbers.push(group);
  }

  removePhoneNumber(index, selectedNumber) {
    if (selectedNumber.value["id"] > 0) {
      selectedNumber.get("deleted").setValue(true);
      selectedNumber.get("contactName").clearValidators();
      selectedNumber.get("title").clearValidators();
      selectedNumber.get("office").clearValidators();
      selectedNumber.get("mobile").clearValidators();
      selectedNumber.get("fax").clearValidators();
      selectedNumber.get("email").clearValidators();
    }
    this.phoneNumbers.removeAt(index);
    this.onContactRemoved.emit(true);
  }

  filterDeletedControls(controls: AbstractControl[]): AbstractControl[] {
    return controls.filter(
      (c) =>
        c.value["deleted"] == false ||
        c.value["deleted"] == "" ||
        c.value["deleted"] == null
    );
  }

  /**
   * CO-3228 / REQ-007, REQ-008 — the default value of "Billable Room and Board"
   * for a facility type.
   *
   * Q5009 (UNSPECIFIED) is special: it is the only default-checked type that is
   * NPI-exempt when R&B is unchecked (REQ-009), so defaulting it checked with no
   * NPI would manufacture a facility that is R&B-billable but cannot produce a
   * valid R&B claim — and CO-3229 would then block a save the operator never
   * asked for. Its default is therefore derived from NPI presence (AC-007-4).
   * Presence, not validity: a malformed NPI is a format error the operator is
   * already being shown, not a reason to silently flip the flag off.
   *
   * This is a DEFAULT, not a rule. It is applied when the operator changes the
   * facility type, and never re-derived on load — see changeFacilityType() and
   * the note in mapFacilityToFormGroup().
   */
  rnbDefaultFor(facilityType: string, npi: string): boolean {
    return GeneralFacility.isAlwaysRnbBillable(facilityType);
  }

  /**
   * Applies the R&B lock state for a facility type, setting both the disabled flag
   * the template binds and — when locked — the checkbox value itself.
   *
   * Product, 2026-08-03: the always-billable types show a checked, grayed box; it
   * is not an operator decision. Q5001, Q5002 and Q5009 stay editable and are
   * never grayed.
   *
   * Why locked types are forced CHECKED rather than showing the stored value:
   * ~410 production facilities of these types currently have isEligibleForRNB
   * false (Q5005–Q5008 were not previously default-checked). Rendering the stored
   * false would give a box that is both unchecked AND grayed — stating the
   * facility is not R&B-billable while refusing to let anyone change it. Forcing
   * checked keeps the control honest about the rule, and makes the screen
   * self-healing: opening and saving such a facility corrects its stored flag.
   *
   * Consequence worth knowing: for a locked-type facility with no valid NPI this
   * makes the NPI required immediately (CO-3229's union rule), so the operator
   * sees a validation error on a record they have not edited. That is correct —
   * such a facility cannot produce an R&B claim with an operating provider — but
   * it is visible, and it affects the 146 prod facilities of these types that
   * currently have no NPI at all.
   */
  applyRnbLockState(facilityType: string): void {
    // Gated on the feature flag like the rest of CO-2419. Without this the locked
    // checkbox and the forced-checked value would appear for every tenant the
    // moment this deploys, which is exactly what the dark release exists to
    // prevent — and it would rewrite stored flags on types the pre-CO-2419 UI
    // never even showed a checkbox for.
    if (!this.q5001Enabled) {
      this.isBillableRNBLocked = false;
      return;
    }
    this.isBillableRNBLocked =
      GeneralFacility.isAlwaysRnbBillable(facilityType);
    if (this.isBillableRNBLocked) {
      this.isBillableRNB = true;
    }
  }

  /**
   * CO-3238 / REQ-029 (BDD 1.3) + CO-3240 / REQ-027 (BDD 9.3) — whether Q5001 is
   * the selected type. Drives both the informational hint and the rationale-note
   * field; see the note on q5001Selected for why that is one flag and not two.
   *
   * Gated on q5001Enabled like everything else in CO-2419. That gate is not
   * redundant with filterQ5001(): "HOME" is a PRE-EXISTING facility type in
   * commonVar.FACILITY_TYPES, so facilities already stored with facilityType
   * "HOME" exist independently of this feature. Without the flag check, deploying
   * this would start showing Q5001 guidance to every tenant on records that
   * predate the epic — the exact thing the dark release exists to prevent.
   *
   * `announce` is false on load and true on operator change; see
   * q5001HintAnnouncement.
   */
  applyQ5001Selection(facilityType: string, announce: boolean): void {
    const next =
      this.q5001Enabled && facilityType === GeneralFacility.Q5001_FACILITY_TYPE;
    if (announce && next !== this.q5001Selected) {
      // Empty when the hint goes away: there is nothing to say about no longer
      // having selected Q5001, and a screen reader should not narrate a removal.
      this.q5001HintAnnouncement = next ? GeneralFacility.Q5001_HINT : "";
    }
    this.q5001Selected = next;
  }

  /**
   * CO-3240 / REQ-026 (BDD 9.2) — shows or hides the non-blocking warning that the
   * R&B flag has been moved off its type default.
   *
   * WHAT "DIVERGES" MEANS NOW, which is not what BDD 9.2's example says. 9.2 was
   * written against the pre-CO-3228 model and illustrates the case as "operator
   * unchecks R&B on an R&B-eligible type (e.g., Skilled Nursing Facility)". That
   * case no longer exists: CO-3228 made the always-billable types (Q5003–Q5008,
   * Q5010) checked and GRAYED, so an operator cannot uncheck a Skilled Nursing
   * Facility at all. The only remaining divergence is the opposite direction —
   * CHECKING the box on one of the three editable types (Q5001, Q5002, Q5009),
   * whose default is unchecked.
   *
   * That direction is the one worth warning about anyway: it opts a residence,
   * assisted-living or unspecified location into generating R&B claims, and it
   * makes the NPI required (CO-3229's union rule). REQ-026's intent — "reduce
   * accidental misconfiguration" — is served, its example is stale.
   *
   * NON-BLOCKING, and by construction rather than by discipline: this method sets
   * display state only. It touches no form control, no validator, and not
   * saveEnabled, so it cannot gate a save (9.2's "save still proceeds").
   *
   * The isBillableRNBLocked check is redundant with the comparison — a locked type
   * defaults true and is forced true, so it can never diverge — and is kept
   * because a warning about a control the operator cannot reach would be a bug
   * worth failing loudly on if the lock rules ever change.
   */
  applyRnbOverrideWarning(facilityType: string, announce: boolean): void {
    const next =
      this.q5001Enabled &&
      this.showIsBillable &&
      !this.isBillableRNBLocked &&
      this.isBillableRNB !== this.rnbDefaultFor(facilityType, "");
    if (announce && next !== this.showRnbOverrideWarning) {
      this.rnbOverrideAnnouncement = next
        ? GeneralFacility.RNB_OVERRIDE_WARNING
        : "";
    }
    this.showRnbOverrideWarning = next;
  }

  /**
   * CO-3240 / REQ-027 — drops the rationale note when Q5001 is no longer selected.
   *
   * The alternative is leaving it, and leaving it is worse: the note's entire
   * content is "why this facility is billed Q5001", so on a facility that is now
   * Q5003 it is a false statement sitting in the record, in the audit diff, and in
   * any report that surfaces the field. The operator's cue is that the field
   * disappears from the screen at the same moment.
   */
  private clearQ5001NoteIfNotApplicable(): void {
    if (!this.q5001Selected && this.innerFacilityForm) {
      this.innerFacilityForm.get("q5001RationaleNote").setValue("");
    }
  }

  changeFacilityType(val) {
    if (this.q5001Enabled) {
      // CO-3228 / REQ-006: the control is available for every type, not only
      // Q5009. Hidden until a type is chosen, because the default needs one.
      this.showIsBillable = !!val;
      // REQ-007/008: apply the type default. This is the only place it is
      // applied, which is what lets a stored value survive load (AC-006-3) and
      // an operator override survive a save (REQ-007 AC-007-3) without any
      // "was overridden" bookkeeping. It also satisfies AC-008-2 — changing a
      // previously R&B-billable facility to Q5001 resets the flag to unchecked.
      this.isBillableRNB = this.rnbDefaultFor(
        val,
        this.innerFacilityForm ? this.innerFacilityForm.get("npi").value : ""
      );
      this.applyRnbLockState(val);
      if (this.currentFacility) {
        this.currentFacility.isEligibleForRNB = this.isBillableRNB;
        (this.currentFacility as any).medicaidFacilitySubtype =
          this.medicaidFacilitySubtype || "";
        (this.currentFacility as any).medicaidFacilitySubtype =
          this.medicaidFacilitySubtype || "";
      }
      // The default just changed whether an NPI is required (CO-3229).
      if (this.innerFacilityForm) {
        this.checkNpiValue();
      }
    } else {
      if (val === "UNSPECIFIED") this.showIsBillable = true;
      else this.showIsBillable = false;
    }

    // CO-3238 / REQ-029: outside the flag branch above because applyQ5001Selection
    // applies the gate itself, and because the hint must be REMOVED when the
    // operator changes the type away from Q5001 (BDD 1.3, final clause) —
    // including on the paths that fall through the else.
    this.applyQ5001Selection(val, true);
    // CO-3240 / REQ-026: after applyRnbLockState and the default assignment above,
    // so it compares the flag's NEW value against the NEW type's default. Changing
    // type always resets the flag to that default, so this normally clears the
    // warning — which is right: the operator has not overridden anything yet.
    this.applyRnbOverrideWarning(val, true);
    // CO-3240 / REQ-027: a rationale for choosing Q5001 is wrong data on a
    // facility that is no longer Q5001, and it would sit in the audit trail and any
    // report as stale justification. Cleared on the way out; the field visibly
    // disappears at the same moment, which is the operator's cue.
    this.clearQ5001NoteIfNotApplicable();

    if (this.currentFacility) {
      this.currentFacility.facilityType = val;
    }
    this.onBillableChange.emit();
  }

  billableChange() {
    // CO-3228: guarded. Generalising the checkbox to every facility type widened
    // this method's reach — it used to be reachable only on a Q5009 facility, and
    // is now reachable on a brand-new facility the parent has not populated.
    if (this.currentFacility) {
      this.currentFacility.isEligibleForRNB = this.isBillableRNB;
      (this.currentFacility as any).medicaidFacilitySubtype =
        this.medicaidFacilitySubtype || "";
    }
    // CO-3229: toggling R&B changes whether the NPI is required, and the checkbox
    // is bound with ngModel rather than as a form control, so its change does not
    // reach the form's valueChanges. Without this the requirement would go stale.
    this.checkNpiValue();
    // CO-3240 / REQ-026: this is the call that actually matters. Checking the box
    // on one of the three editable types is the only way to diverge from the type
    // default now that CO-3228 grays the always-billable types.
    this.applyRnbOverrideWarning(
      this.innerFacilityForm
        ? this.innerFacilityForm.get("facilityType").value
        : "",
      true
    );
    this.onBillableChange.emit();
  }

  searchCounty() {
    this.countyList = [];
    let zipCode = this.innerFacilityForm.get("zip").value;
    let state = this.innerFacilityForm.get("state").value;
    if (zipCode && zipCode.length === 5 && state) {
      this.facilityService.getCounties(state, zipCode).subscribe(
        (response) => {
          if (response && response.length > 0) {
            this.countyList = response;
          } else {
            this.innerFacilityForm.get("county").setValue(null);
            this.countyList = [];
          }
        },
        (err) => {
          console.log(
            "Error while fetching county for zip code entered " + err
          );
          this.innerFacilityForm.get("county").setValue(null);
          this.countyList = [];
        }
      );
    } else {
      this.innerFacilityForm.get("county").setValue(null);
      this.countyList = [];
    }
  }

  checkChar(event: any, type) {
    let newValue = event.target.value;
    if (type == "alpha") {
      let regExp = new RegExp("^[A-Za-z? ]+$");
      if (!regExp.test(newValue)) {
        event.target.value = newValue.replace(/[^A-Za-z ]/g, "");
      }
    } else if (type == "alphaNumeric") {
      let regExp = new RegExp("^[A-Za-z0-9? ]+$");
      if (!regExp.test(newValue)) {
        event.target.value = newValue.replace(/[^A-Za-z0-9 ]/g, "");
      }
    } else if (type == "numeric") {
      let regExp = new RegExp("^[0-9]+$");
      if (!regExp.test(newValue)) {
        event.target.value = newValue.replace(/[^0-9 ]/g, "");
      }
    }
    this.checkCanSave();
  }

  initInnerFacilityForm() {
    this.mapFacilityToFormGroup();

    let now = new Date();
    //const today = { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
    const tenYearsLater = {
      year: now.getFullYear() + 10,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
    const eightyYearsAgo = {
      year: now.getFullYear() - 80,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };

    this.effectiveDateMin = eightyYearsAgo;
    this.effectiveDateMax = tenYearsLater;

    this.subscriptions.push(
      this.innerFacilityForm.valueChanges.subscribe(this.onFormChanges),
      // CO-3229: was an inline copy of the two-type exemption that also asserted
      // {required: true} unconditionally, so changing to a non-exempt type marked
      // the NPI invalid even when one was already entered. One rule, one place.
      this.innerFacilityForm
        .get("facilityType")
        .valueChanges.subscribe(() => this.checkNpiValue())
    );
  }

  mapFacilityToFormGroup() {
    let facilityName = "";
    let facilityType = "";
    let npi = "";
    let address1 = "";
    let address2 = "";
    let city = "";
    let state = "";
    let zip = "";
    let zipCode = "";
    let faxNumber = "";
    let county = "";
    let facilityPhoneNumber = "";
    let emailAddress = "";
    let medicaidNumber = "";
    let taxonomyCode = "";
    let licenseNumber = "";
    let totalBeds = "";
    let marEnabled = false;
    let q5001RationaleNote = "";

    if (this.currentFacility) {
      facilityName =
        this.currentFacility.facilityName &&
        this.currentFacility.facilityName.length > 0
          ? this.currentFacility.facilityName
          : "";
      facilityType =
        this.currentFacility.facilityType &&
        this.currentFacility.facilityType.length > 0
          ? this.currentFacility.facilityType
          : "";
      npi =
        this.currentFacility.npi && this.currentFacility.npi.length > 0
          ? this.currentFacility.npi
          : "";
      faxNumber =
        this.currentFacility.hipaaPatientFax &&
        this.currentFacility.hipaaPatientFax > 0
          ? this.formatPhoneNumber(this.currentFacility.hipaaPatientFax)
          : "";
      facilityPhoneNumber =
        this.currentFacility.phoneNumber &&
        this.currentFacility.phoneNumber.length > 0
          ? this.currentFacility.phoneNumber
          : "";
      emailAddress =
        this.currentFacility.emailAddress &&
        this.currentFacility.emailAddress > 0
          ? this.currentFacility.emailAddress
          : "";
      medicaidNumber =
        this.currentFacility.medicaidNumber &&
        this.currentFacility.medicaidNumber.length > 0
          ? this.currentFacility.medicaidNumber
          : "";
      taxonomyCode =
        this.currentFacility.taxonomyCode &&
        this.currentFacility.taxonomyCode.length > 0
          ? this.currentFacility.taxonomyCode
          : "";
      licenseNumber =
        this.currentFacility.licenseNumber &&
        this.currentFacility.licenseNumber.length > 0
          ? this.currentFacility.licenseNumber
          : "";
      totalBeds =
        this.currentFacility.totalBeds && this.currentFacility.totalBeds > 0
          ? this.currentFacility.totalBeds
          : "";
      marEnabled =
        this.currentFacility.marEnabled != null
          ? this.currentFacility.marEnabled
          : false;
      // CO-3229: was this.currentFacility.IsEligibleForRNB — capital I, while the
      // JSON field is isEligibleForRNB, so this always produced undefined. It was
      // masked because ngOnInit re-assigns from the correct casing a few lines
      // later, but the NPI union rule reads isBillableRNB DURING form construction
      // (below), which makes the casing load-bearing.
      this.isBillableRNB = !!this.currentFacility.isEligibleForRNB;
      this.applyRnbLockState(this.currentFacility.facilityType);
      // CO-3240 / REQ-027: read straight through, not re-derived. A stored note is
      // the operator's own words and nothing here has standing to rewrite them.
      q5001RationaleNote = this.currentFacility.q5001RationaleNote
        ? this.currentFacility.q5001RationaleNote
        : "";

      if (this.currentFacility.streetAddress) {
        address1 =
          this.currentFacility.streetAddress.address1 &&
          this.currentFacility.streetAddress.address1.length > 0
            ? this.currentFacility.streetAddress.address1
            : "";
        address2 =
          this.currentFacility.streetAddress.address2 &&
          this.currentFacility.streetAddress.address2.length > 0
            ? this.currentFacility.streetAddress.address2
            : "";
        city =
          this.currentFacility.streetAddress.city &&
          this.currentFacility.streetAddress.city.length > 0
            ? this.currentFacility.streetAddress.city
            : "";
        state =
          this.currentFacility.streetAddress.state &&
          this.currentFacility.streetAddress.state.length > 0
            ? this.currentFacility.streetAddress.state
            : "";
        zip =
          this.currentFacility.streetAddress.zipCode &&
          this.currentFacility.streetAddress.zipCode.length > 0
            ? this.currentFacility.streetAddress.zipCode.slice(0, 5)
            : "";
        zipCode =
          this.currentFacility.streetAddress.zipCode &&
          this.currentFacility.streetAddress.zipCode.length > 5
            ? this.currentFacility.streetAddress.zipCode.slice(5)
            : "";
        county = this.currentFacility.streetAddress.county
          ? this.currentFacility.streetAddress.county
          : "";
      }

      this.isNPIRequired = this.npiRequiredFor(
        facilityType,
        this.isBillableRNB
      );
      // CO-3228 / REQ-006: available for every type once the flag is on. Note
      // what is deliberately NOT here — the type default. isBillableRNB was set
      // from the STORED isEligibleForRNB above, and re-deriving it from type on
      // load would silently rewrite an operator's saved choice the first time
      // anyone opened the facility (AC-006-3 / BDD 3.1b).
      if (this.q5001Enabled) {
        this.showIsBillable = !!facilityType;
      } else if (facilityType === "UNSPECIFIED") {
        this.showIsBillable = true;
      }
    }

    this.innerFacilityForm = this.formBuilder.group({
      initialized: [null],
      facilityName: [facilityName, [Validators.required]],
      facilityType: [facilityType, [Validators.required]],
      // CO-3229: no declared validator. checkNpiValue() calls setErrors() on this
      // control, which overrides declared validators — the Validators.pattern that
      // used to sit here never took effect. The requirement and format rules both
      // live in checkNpiValue() now.
      npi: [npi],
      address1: [address1, [Validators.required]],
      address2: [address2],
      city: [city, [Validators.required, Validators.pattern(this.alphaRegex)]],
      state: [state, [Validators.required]],
      zip: [
        zip,
        [
          Validators.required,
          Validators.pattern(this.numberRegex),
          Validators.minLength(5),
        ],
      ],
      zipCode: [
        zipCode,
        [
          Validators.required,
          Validators.pattern(this.numberRegex),
          Validators.minLength(4),
        ],
      ],
      faxNumber: [faxNumber, [Validators.minLength(13)]],
      county: [county, [Validators.required]],
      facilityPhoneNumber: [
        facilityPhoneNumber,
        [Validators.minLength(13), Validators.required],
      ],
      emailAddress: [
        emailAddress,
        [Validators.pattern(this.emailRegex), Validators.maxLength(50)],
      ],
      phoneNumbers: new FormArray([]),
      medicaidNumber: [
        medicaidNumber,
        [Validators.pattern(this.alphaNumericRegx), Validators.maxLength(15)],
      ],
      taxonomyCode: [taxonomyCode, [Validators.pattern(this.alphaNumericRegx)]],
      licenseNumber: [
        licenseNumber,
        [Validators.pattern(this.alphaNumericRegx)],
      ],
      totalBeds: [totalBeds, [Validators.pattern(this.numberRegex)]],
      marEnabled: [marEnabled],
      // CO-3240 / REQ-027: a real form control rather than a standalone ngModel, so
      // the value travels through mapFacilityForm() with every other field. The
      // maxLength mirrors the max=500 on Facility.Q5001RationaleNote in
      // facility-functions — enforced in both places because the API rejecting a
      // save the UI allowed would surface as a failure with nothing on screen
      // explaining it.
      q5001RationaleNote: [
        q5001RationaleNote,
        [Validators.maxLength(GeneralFacility.Q5001_NOTE_MAX_LENGTH)],
      ],
      perDiemRate: [null],
      effectiveDate: [null],
    });

    if (this.currentFacility) {
      this.mapContactsToFormGroup();
      this.searchCounty();
    } else {
      this.addPhoneNumber();
    }
  }

  mapContactsToFormGroup() {
    if (
      this.currentFacility.facilityContacts &&
      this.currentFacility.facilityContacts.length
    ) {
      for (let facilityContact of this.currentFacility.facilityContacts) {
        const facilityContactGroup = this.formBuilder.group({
          contactName: [
            facilityContact.name ? facilityContact.name : "",
            Validators.pattern(this.alphaNumericRegx),
          ],
          title: [
            facilityContact.title ? facilityContact.title : "",
            Validators.pattern(this.alphaNumericRegx),
          ],
          office: [
            facilityContact.officePhone
              ? this.formatPhoneNumber(facilityContact.officePhone)
              : "",
            [Validators.minLength(13)],
          ],
          mobile: [
            facilityContact.mobilePhone
              ? this.formatPhoneNumber(facilityContact.mobilePhone)
              : "",
            [Validators.minLength(13)],
          ],
          fax: [
            facilityContact.faxNumber
              ? this.formatPhoneNumber(facilityContact.faxNumber)
              : "",
            [Validators.minLength(13)],
          ],
          email: [
            facilityContact.email ? facilityContact.email : "",
            [Validators.pattern(this.emailRegex)],
          ],
          id: [null],
          deleted: [false],
        });
        this.phoneNumbers.push(facilityContactGroup);
      }
    }
  }

  mapCountyToFormGroup() {
    if (
      this.currentFacility.streetAddress &&
      this.currentFacility.streetAddress.county &&
      this.currentFacility.streetAddress.county.length > 0
    ) {
      this.countyList = [
        {
          display: this.currentFacility.streetAddress.county,
          value: this.currentFacility.streetAddress.county,
        },
      ];
      this.innerFacilityForm.controls["county"].setValue(
        this.currentFacility.streetAddress.county
      );
      this.innerFacilityForm.controls["county"].markAsTouched();
    }
  }

  saveFacility() {
    this.checkCanSave();
    if (this.saveEnabled) {
      let obj = this.mapFacilityForm(this.innerFacilityForm.controls);
      this.onFacilitySaved.emit(obj);
      this.saveEnabled = false;
    } else {
      // CO-3229: name the NPI problem when that is what blocked the save. This
      // template renders no inline validation messages for any of its ~20 fields,
      // so this alert is the only channel available; BDD 3.4 asks for the message
      // "inline", which would mean introducing an inline-message pattern this
      // component does not have. Flagged for a UX decision — see the PR.
      const npiMessage = this.npiErrorMessage();
      this.showAlert({
        title: "Invalid Submission",
        message: npiMessage
          ? npiMessage
          : "Missing required fields. Please review.",
      });
    }
  }

  cancel() {
    this.onCanceled.emit(true);
  }

  onFormChanges = (value) => {
    if (value) {
      this.checkNpiValue();
      this.checkCanSave();
      this.onTouched.emit(this.innerFacilityForm.dirty);
    }
  };

  checkCanSave() {
    this.saveEnabled = this.innerFacilityForm && this.innerFacilityForm.valid;
  }

  /**
   * CO-3229 / REQ-009 — the union rule. NPI is required when EITHER "Billable
   * Room and Board" is checked (the facility NPI is the operating provider on the
   * R&B claim) OR the facility type requires one regardless.
   *
   * An unrecognised type falls on the required side: a typo must not buy an
   * exemption.
   */
  npiRequiredFor(facilityType: string, isBillableRNB: boolean): boolean {
    if (isBillableRNB) {
      return true;
    }
    return (
      GeneralFacility.NPI_EXEMPT_FACILITY_TYPES.indexOf(facilityType) === -1
    );
  }

  /**
   * The message for whatever is currently wrong with the NPI, or "".
   *
   * Three distinct messages rather than one: the reason determines what the user
   * has to do. A bare "NPI is required" on a Q5001 facility reads as a
   * contradiction — the type is exempt, so nothing on screen explains the error
   * unless the message names Room & Board.
   */
  npiErrorMessage(): string {
    const errors = this.innerFacilityForm.get("npi").errors;
    if (!errors) {
      return "";
    }
    const keys = Object.keys(GeneralFacility.NPI_MESSAGES);
    for (let i = 0; i < keys.length; i++) {
      if (errors[keys[i]]) {
        return GeneralFacility.NPI_MESSAGES[keys[i]];
      }
    }
    return "";
  }

  /**
   * Recompute the NPI requirement and format state. Authoritative: it sets the
   * control's errors outright, so it must decide BOTH questions.
   *
   * That is not a style choice. This method already ran on every valueChanges and
   * called setErrors(null) whenever a value was present, and setErrors overrides
   * the declared validators — so Validators.pattern(NPI_REGEX) on the npi control
   * was dead and the form accepted a 3-digit NPI. The pattern validator has been
   * removed rather than left in place looking load-bearing, and the format check
   * lives here instead.
   *
   * Format is a 10-digit Luhn check (utils/npi.ts), matching IsValidNpi() in
   * facility-functions. Unlike the Company Location form, a 9-digit value is NOT
   * accepted-and-derived here: this form sends form.npi.value straight through, so
   * anything short of 10 digits would be rejected by the API anyway.
   */
  checkNpiValue() {
    const facilityType = this.innerFacilityForm.get("facilityType").value;
    const value = this.innerFacilityForm.get("npi").value;

    this.setNpiRequired(this.npiRequiredFor(facilityType, this.isBillableRNB));

    let errors = null;
    if (!value) {
      if (this.isNPIRequired) {
        errors = this.isBillableRNB
          ? { npiRequiredRnb: true }
          : { npiRequiredType: true };
      }
    } else if (!isValidNpi(value)) {
      // Optional does not mean unvalidated — a malformed NPI on an exempt
      // facility still reaches a claim.
      errors = { npiFormat: true };
    }

    this.innerFacilityForm.controls["npi"].setErrors(errors);
    this.innerFacilityForm.controls["npi"].markAsTouched();
    this.cd.detectChanges();
  }

  /**
   * CO-3237 / REQ-018 (BDD 8.1) — set isNPIRequired and, when it CHANGES, put the
   * change into the polite live region so a screen-reader user hears it.
   *
   * Only the transition is announced. checkNpiValue() runs on every valueChanges,
   * so announcing the state rather than the change would re-speak "NPI is
   * required" on every keystroke and the user would turn the page off.
   *
   * The load path in mapFacilityToFormGroup() deliberately assigns isNPIRequired
   * directly instead of calling this: opening a facility is not a change, and the
   * label already carries the state for anyone who reaches the field.
   */
  private setNpiRequired(next: boolean): void {
    if (next !== this.isNPIRequired) {
      this.npiRequirementAnnouncement = next
        ? "NPI is now required for this facility."
        : "NPI is no longer required for this facility.";
    }
    this.isNPIRequired = next;
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
          handler: () => {},
        },
      ],
    });
    alert.present();
  };

  mapFacilityForm(form: any) {
    if (form) {
      const today = new Date();
      const fd: FacilityDetail = {} as any as FacilityDetail;
      if (this.currentFacility != null) {
        fd.facilityId = this.currentFacility.facilityId;
      }
      fd.facilityName = form.facilityName.value;
      fd.facilityType = form.facilityType.value;
      fd.npi = form.npi.value;
      const sa: StreetAddress = {} as any as StreetAddress;
      sa.address1 = form.address1.value;
      sa.address2 = form.address2.value;
      sa.city = form.city.value;
      sa.state = form.state.value;
      sa.zipCode = form.zip.value + form.zipCode.value;
      sa.county = form.county.value;
      fd.streetAddress = sa;
      fd.hipaaPatientFax =
        form.faxNumber.value.length > 0
          ? this.getNumber(form.faxNumber.value)
          : null;
      fd.phones = [];
      const ph: Phone = {} as any as Phone;
      ph.isPrimary = true;
      ph.number = this.getNumber(form.facilityPhoneNumber.value);
      fd.phones.push(ph);
      fd.facilityContacts = [];
      if (form.phoneNumbers.value) {
        for (let phone of form.phoneNumbers.value) {
          if (
            phone.contactName ||
            phone.title ||
            phone.fax ||
            phone.mobile ||
            phone.office ||
            phone.email
          ) {
            const fc: FacilityContact = {} as any as FacilityContact;
            fc.name = phone.contactName;
            fc.title = phone.title;
            fc.faxNumber =
              phone.fax != null && phone.fax.length > 0
                ? this.getNumber(phone.fax)
                : "";
            fc.mobilePhone =
              phone.mobile != null && phone.mobile.length > 0
                ? this.getNumber(phone.mobile)
                : "";
            fc.officePhone =
              phone.office != null && phone.office.length > 0
                ? this.getNumber(phone.office)
                : "";
            fc.email = phone.email;
            fd.facilityContacts.push(fc);
          }
        }
      }
      fd.medicaidNumber = form.medicaidNumber.value;
      fd.taxonomyCode = form.taxonomyCode.value;
      fd.licenseNumber =
        form.licenseNumber.value.length > 0 ? form.licenseNumber.value : null;
      fd.totalBeds = Number(form.totalBeds.value);
      fd.marEnabled = form.marEnabled.value;
      fd.perDiemRate = Number(form.perDiemRate.value);
      fd.effectiveDate = today.toISOString().split("T")[0];
      // CO-3228 / REQ-006 AC-006-2: the flag persists for every facility type.
      // It used to be forced false for anything but Q5009, which meant the
      // checkbox could not have been honoured for the other nine types even if
      // it had been rendered.
      if (this.q5001Enabled) {
        fd.isEligibleForRNB = this.isBillableRNB;
      } else if (fd.facilityType === "UNSPECIFIED") {
        fd.isEligibleForRNB = this.isBillableRNB;
      } else {
        fd.isEligibleForRNB = false;
      }
      // CO-3240 / REQ-027 (AC-027-1): persisted with the facility. Sent only when
      // Q5001 is the selected type — anything else and the note is stale
      // justification, so it goes out empty and the omitempty tags in
      // facility-functions drop the attribute. Also gated on q5001Enabled: with the
      // flag off the field is never rendered, so a payload carrying it could only
      // come from state the operator could not see.
      fd.q5001RationaleNote = this.q5001Selected
        ? form.q5001RationaleNote.value || ""
        : "";
      return fd;
    }
    return;
  }

  getNumber(number) {
    var numb = number.match(/\d/g);
    numb = numb.join("");
    return numb;
  }

  formatPhoneNumber(phoneNumber) {
    if (phoneNumber.length !== 10) {
      return "";
    }
    return `(${phoneNumber.slice(0, 3)})${phoneNumber.slice(
      3,
      6
    )}-${phoneNumber.slice(6)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/facilities/general-facility/general-facility.ts